import { useEffect, useState } from 'react';
import type { MessageFormatElement } from 'react-intl';

import { SupportedLocale } from '@/utils/i18n/supportedLocales';
import { logger } from '@/utils/logger/logger';

const pseudoLocLog = '[intl:pseudoloc]';

type LocaleMessages = Record<
  string,
  | { message: string; description?: string }
  | { defaultMessage: string; description?: string }
>;

/**
 * In-memory AST cache for {@link SupportedLocale.EnPs} (canonical English catalog → pseudolocalised ICU).
 */
const pseudoLocAstFromEnCatalogCache = new Map<
  SupportedLocale,
  Record<string, MessageFormatElement[]>
>();

/** Clears the pseudoloc AST cache between Jest cases (no-op outside `NODE_ENV=test`). */
export function resetPseudoLocAstCacheForTests(): void {
  if (process.env.NODE_ENV === 'test') {
    pseudoLocAstFromEnCatalogCache.clear();
  }
}

/**
 * Loads and caches pseudolocalised ICU message ASTs from `en/messages.json` when {@link SupportedLocale.EnPs} is active.
 */
export function usePseudoLocMessages(
  activeLocale: SupportedLocale
): Record<string, MessageFormatElement[]> | null {
  const [pseudoLocAstMessages, setPseudoLocAstMessages] = useState<Record<
    string,
    MessageFormatElement[]
  > | null>(null);

  useEffect(() => {
    if (activeLocale !== SupportedLocale.EnPs) {
      setPseudoLocAstMessages(null);
      return;
    }

    const cachedAst = pseudoLocAstFromEnCatalogCache.get(SupportedLocale.EnPs);
    if (cachedAst) {
      logger.debug(pseudoLocLog, {
        event: 'pseudo_ast_memory_cache',
        activeLocale,
        pseudoSource: 'en/messages.json',
        keyCount: Object.keys(cachedAst).length,
      });
      setPseudoLocAstMessages(cachedAst);
      return;
    }

    let cancelled = false;

    const loadPseudoLoc = async () => {
      logger.debug(pseudoLocLog, {
        event: 'pseudo_build_start',
        activeLocale,
        source: 'en/messages.json',
      });
      try {
        const [{ generateENXD }, { parse }, enMod] = await Promise.all([
          import('pseudolocalise'),
          import('@formatjs/icu-messageformat-parser'),
          import('@/locales/en/messages.json'),
        ]);
        const source = (enMod.default ?? enMod) as LocaleMessages;

        const astMessages: Record<string, MessageFormatElement[]> = {};
        for (const [id, value] of Object.entries(source)) {
          const raw =
            value && typeof value === 'object' && 'message' in value
              ? value.message
              : value && typeof value === 'object' && 'defaultMessage' in value
                ? value.defaultMessage
                : '';
          if (typeof raw === 'string' && raw.length > 0) {
            try {
              astMessages[id] = generateENXD(parse(raw));
            } catch {
              try {
                astMessages[id] = generateENXD(raw);
              } catch {
                logger.debug(pseudoLocLog, { event: 'pseudo_key_skip', id });
              }
            }
          }
        }

        if (!cancelled) {
          pseudoLocAstFromEnCatalogCache.set(SupportedLocale.EnPs, astMessages);
          setPseudoLocAstMessages(astMessages);
          logger.debug(pseudoLocLog, {
            event: 'pseudo_build_done',
            activeLocale,
            astKeyCount: Object.keys(astMessages).length,
          });
        } else {
          logger.debug(pseudoLocLog, {
            event: 'pseudo_build_cancelled',
            activeLocale,
          });
        }
      } catch (error) {
        logger.error('Failed to build pseudolocalisation', error);
      }
    };

    void loadPseudoLoc();

    return () => {
      cancelled = true;
    };
  }, [activeLocale]);

  return pseudoLocAstMessages;
}
